import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Shield,
  Lock,
  Verified,
  Security,
  Cookie
} from '@mui/icons-material';

// ==========================
// POPUP DE PRIVACIDADE
// ==========================
export const PrivacidadeDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" gap={1}>
        <Shield color="primary" />
        <Typography variant="h5">Política de Privacidade</Typography>
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" gutterBottom color="primary">
        🔒 Como Protegemos Seus Dados
      </Typography>
      
      <List>
        <ListItem>
          <ListItemIcon>
            <Lock color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Criptografia AES-256-GCM"
            secondary="Todos os dados sensíveis são criptografados antes de serem armazenados. Cada registro possui chave única."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Lock color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Senhas com Bcrypt (12 rounds)"
            secondary="Senhas nunca são armazenadas em texto plano. Usamos algoritmo bcrypt com 12 rounds de hashing."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Security color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Certificado SSL/TLS"
            secondary="Toda comunicação entre você e nossos servidores é criptografada com HTTPS."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Verified color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Dados armazenados no Brasil"
            secondary="Servidores localizados em território nacional (Hostinger VPS em São Paulo)."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Shield color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Controle de acesso rigoroso"
            secondary="Sistema de permissões por nível: Admin, Psicólogo e Recepcionista. Cada usuário vê apenas o necessário."
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h6" gutterBottom color="primary">
        📋 O Que NÃO Fazemos
      </Typography>
      
      <List dense>
        <ListItem>
          <Typography>❌ Não compartilhamos seus dados com terceiros</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Não vendemos informações dos pacientes</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Não usamos dados para fins publicitários</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Não acessamos seus dados sem autorização</Typography>
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="body2" color="text.secondary">
        <strong>Transparência:</strong> Todos os acessos ao sistema são registrados em logs de auditoria para garantir rastreabilidade completa.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" color="primary">
        Entendi
      </Button>
    </DialogActions>
  </Dialog>
);

// ==========================
// POPUP DE TERMOS DE USO
// ==========================
export const TermosDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" gap={1}>
        <Verified color="primary" />
        <Typography variant="h5">Termos de Uso</Typography>
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" gutterBottom color="primary">
        📜 Contrato de Prestação de Serviços
      </Typography>
      
      <List>
        <ListItem>
          <ListItemText
            primary="1. Propriedade dos Dados"
            secondary="Todos os dados inseridos no sistema são de propriedade exclusiva do psicólogo contratante. O Trajetus é apenas o custodiante técnico."
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary="2. Responsabilidades do Trajetus"
            secondary="• Manter o sistema disponível 99,5% do tempo
• Realizar backups diários automáticos
• Garantir segurança e criptografia dos dados
• Fornecer suporte técnico por email e WhatsApp"
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary="3. Responsabilidades do Usuário"
            secondary="• Manter senha segura e confidencial
• Não compartilhar acesso com terceiros não autorizados
• Respeitar sigilo profissional (Código de Ética do Psicólogo)
• Realizar logout ao deixar computador desabloqueado"
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary="4. Período de Teste"
            secondary="30 dias grátis, sem necessidade de cartão de crédito. Cancele quando quiser, sem multa."
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary="5. Cancelamento"
            secondary="Pode ser solicitado a qualquer momento. Seus dados ficam disponíveis para download por 90 dias após o cancelamento."
          />
        </ListItem>
        
        <ListItem>
          <ListItemText
            primary="6. Atualizações"
            secondary="O sistema é atualizado regularmente com novas funcionalidades e correções de segurança, sem custo adicional."
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="body2" color="text.secondary">
        <strong>Importante:</strong> Ao usar o Trajetus, você concorda em utilizar o sistema de acordo com o Código de Ética Profissional do Psicólogo (Resolução CFP nº 10/2005) e a LGPD (Lei 13.709/2018).
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" color="primary">
        Entendi
      </Button>
    </DialogActions>
  </Dialog>
);

// ==========================
// POPUP DE LGPD
// ==========================
export const LGPDDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" gap={1}>
        <Security color="primary" />
        <Typography variant="h5">Conformidade LGPD</Typography>
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" gutterBottom color="primary">
        🇧🇷 Lei Geral de Proteção de Dados (Lei 13.709/2018)
      </Typography>
      
      <Typography variant="body1" paragraph>
        O Trajetus está em <strong>conformidade total</strong> com a LGPD brasileira.
      </Typography>
      
      <List>
        <ListItem>
          <ListItemIcon>
            <Verified color="success" />
          </ListItemIcon>
          <ListItemText
            primary="DPO Designado"
            secondary="Encarregado de Proteção de Dados disponível via email: dpo@trajetus.net"
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Verified color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Base Legal: Legítimo Interesse (Art. 7º, IX)"
            secondary="Tratamento de dados necessário para prestação do serviço contratado."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Verified color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Minimização de Dados"
            secondary="Coletamos apenas dados estritamente necessários para o funcionamento do sistema."
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Verified color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Segurança da Informação"
            secondary="Medidas técnicas (criptografia, firewall) e administrativas (controle de acesso) implementadas."
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h6" gutterBottom color="primary">
        ✅ Direitos dos Titulares (Art. 18)
      </Typography>
      
      <List dense>
        <ListItem>
          <Typography>✓ Confirmação de tratamento de dados</Typography>
        </ListItem>
        <ListItem>
          <Typography>✓ Acesso aos dados (exportação)</Typography>
        </ListItem>
        <ListItem>
          <Typography>✓ Correção de dados incompletos/inexatos</Typography>
        </ListItem>
        <ListItem>
          <Typography>✓ Anonimização/bloqueio/eliminação</Typography>
        </ListItem>
        <ListItem>
          <Typography>✓ Portabilidade dos dados</Typography>
        </ListItem>
        <ListItem>
          <Typography>✓ Revogação do consentimento</Typography>
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="body2" color="text.secondary">
        <strong>Contato LGPD:</strong> Para exercer seus direitos ou tirar dúvidas sobre tratamento de dados, envie email para dpo@trajetus.net
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" color="primary">
        Entendi
      </Button>
    </DialogActions>
  </Dialog>
);

// ==========================
// POPUP DE COOKIES
// ==========================
export const CookiesDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" gap={1}>
        <Cookie color="primary" />
        <Typography variant="h5">Política de Cookies</Typography>
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" gutterBottom color="primary">
        🍪 Cookies Utilizados
      </Typography>
      
      <Typography variant="body1" paragraph>
        O Trajetus utiliza cookies <strong>apenas essenciais</strong> para o funcionamento do sistema.
      </Typography>
      
      <List>
        <ListItem>
          <ListItemIcon>
            <Security color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Cookies Essenciais (Obrigatórios)"
            secondary="• Token de autenticação JWT (válido por 24 horas)
• Dados do usuário logado (nome, email, permissões)
• Preferências de idioma e tema"
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <Security color="info" />
          </ListItemIcon>
          <ListItemText
            primary="Cookies Analíticos (Anônimos)"
            secondary="• Google Analytics para entender uso do sistema
• Dados agregados e anonimizados
• Sem identificação pessoal"
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h6" gutterBottom color="primary">
        ❌ O Que NÃO Usamos
      </Typography>
      
      <List dense>
        <ListItem>
          <Typography>❌ Cookies de rastreamento de terceiros</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Cookies de publicidade</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Cookies de redes sociais</Typography>
        </ListItem>
        <ListItem>
          <Typography>❌ Cookies de perfil comportamental</Typography>
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h6" gutterBottom color="primary">
        🛠️ Controle de Cookies
      </Typography>
      
      <Typography variant="body1" paragraph>
        Você pode gerenciar cookies diretamente no seu navegador:
      </Typography>
      
      <List dense>
        <ListItem>
          <Typography>• <strong>Chrome:</strong> Configurações → Privacidade e segurança → Cookies</Typography>
        </ListItem>
        <ListItem>
          <Typography>• <strong>Firefox:</strong> Opções → Privacidade e segurança → Cookies</Typography>
        </ListItem>
        <ListItem>
          <Typography>• <strong>Safari:</strong> Preferências → Privacidade → Cookies</Typography>
        </ListItem>
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="body2" color="text.secondary">
        <strong>Importante:</strong> Desativar cookies essenciais pode comprometer o funcionamento do sistema (ex: não conseguir fazer login).
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" color="primary">
        Entendi
      </Button>
    </DialogActions>
  </Dialog>
);
